import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Modal, Steps } from 'antd';
import useForm from '../../shared/useFormHook';
import Input from '../../shared/input/Input';
import moment from 'moment';
import { validateStringOnly } from '../../../util/inputValidators';

const { Step } = Steps;
function EducationForm ({ service, customClass }) {
  const { onPanelSelect, onSaveClick, removeEvent, save, setCompleted } = service;
  const { Education } = service.state;
  const [addNew, setAddNew] = useState(false);
  const [newItem, setNewItem] = useState(null);

  const onSave = useCallback(async (isAutoSave) => {
    let listItemsToSave = [...Education];
    if (newItem && addNew && !isAutoSave) listItemsToSave.push(newItem);
    listItemsToSave = listItemsToSave.map(item => item.save(isAutoSave));
    let successResult = true;
    const success = (await Promise.all(listItemsToSave)).every(success => {
      if (success === 'Education') successResult = 'Education';
      return success;
    });
    if (!isAutoSave) {
      if (success) {
        setAddNew(false);
        setNewItem(null);
      } else {
        onPanelSelect('Education');
        setCompleted('Education', false);
      }
    }
    return success ? successResult : false;
  }, [newItem, addNew, Education]);

  useEffect(() => {
    onSaveClick('Education', onSave);
    return () => removeEvent('Education');
  }, [onSave]);

  const addNewItem = () => {
    if (!newItem) {
      const item = {};
      setNewItem(item);
    }
    setAddNew(true);
  };

  const handleCancelAddingNewItem = () => {
    setNewItem(null);
    setAddNew(false);
  };

  return (
    <div className={customClass || ''}>
      <div className="TuringStepsFormv2">
        <p>Professional experience is the heart of a resume, while education indicates that you are formally trained in your field. Tell us about the skills</p>
        <Steps progressDot current={Education.length - 1} direction="vertical">
          {Education.map(item => <EducationItem key={item.id} service={service} item={item} />)}
          {addNew ? (
            <EducationItem service={service} item={newItem} cancelAddingNewItem={handleCancelAddingNewItem} />
          ) : (
            <Step description={<span className="additembtn" onClick={addNewItem}><span className="material-icons" aria-hidden="true">add</span> Add education</span>} />
          )}
        </Steps>
      </div>
      <div className="FinalizeResumeV2FormBtns">
        <Button onClick={() => { setAddNew(false); onPanelSelect(null); }}>Cancel</Button>
        <Button type="primary" onClick={() => save('Education')}>Update information</Button>
      </div>
    </div>
  );
}

EducationForm.propTypes = {
  service: PropTypes.any,
  customClass: PropTypes.string
};

const years = [];
for (let i = 0; i <= 35; i++) {
  years.push({
    value: new Date().getFullYear() - i,
    label: new Date().getFullYear() - i
  });
}

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
].map((month) => ({ value: month, label: month }));

const EducationItem = ({ item, service, ...props }) => {
  const [open, setOpen] = useState(false);
  const errorMessage = 'Only Alphanumeric, accented characters,  : , . and spaces are required';
  const { loadData } = service;
  const { data, bind, setData, getData, setValue, setError, getErrors, resetError, getChanges } = useForm({}, service.saveDebounce);
  const { Education } = service.state;

  useEffect(() => {
    if (!item.id) {
      setOpen(true);
    }
  }, [item.id]);

  useEffect(() => {
    if (!item) return;
    item.start_month = (!isNaN(Number(item.start_month)) ? '' : item.start_month) || '';
    item.end_month = (!isNaN(Number(item.end_month)) ? '' : item.end_month) || '';
    item.start_year = item.start_year || null;
    item.end_year = 'end_year' in item ? item.end_year : null;
    item.degree_error = null;
    item.college_error = null;
    setData(item);
    resetError();

    item.save = async (isAutoSave) => {
      if (!isAutoSave) resetError();
      const fields = ['level', 'degree', 'college', 'start_month', 'start_year'];
      const changedFields = await getChanges(item, [...fields, 'end_month', 'end_year']);
      if (!changedFields.length) return true;

      const data = await getData();
      if (data.end_year !== 0) {
        fields.push('end_month');
        fields.push('end_year');
      }
      if (isAutoSave) {
        if (!data.id || fields.find(field => !data[field])) {
          return false;
        }
      } else {
        fields.forEach(field => {
          if (!data[field] && !["start_month", "end_month"].includes(field)) {
            setError(field);
          }
        });
      }

      if (data.degree && !validateStringOnly(data.degree)) {
        if (isAutoSave) return false;
        setError('degree')
        data.degree_error = errorMessage;
        setData(data);
      }

      if (data.college && !validateStringOnly(data.college)) {
        if (isAutoSave) return false;
        data.college_error =errorMessage;
        setData(data)
        setError('college')
      }

      if (data.start_month && data.start_year && data.end_year && data.end_month) {
        const getDate = (start_end) => {
          const month = data[`${start_end}_month`];
          const year = data[`${start_end}_year`];
          if (month && year) return moment(`${year}-${month}`, 'YYYY-MMMM');
          return null;
        };
        const start_date = getDate('start');
        const end_date = getDate('end');
        if (moment(start_date.format('YYYY-MM-01')).isAfter(end_date.format('YYYY-MM-01'))) {
          if (isAutoSave) return false;
          setError('start_month');
          setError('start_year');
          setError('end_month');
          setError('end_year');
        }
      }

      const errors = await getErrors();
      if (errors.length) {
        if (isAutoSave) return false;
        setOpen(true);
      } else {
        const promiseObj = data.id ? service.updateEducation(data) : service.addEducation(data);
        return promiseObj
          .then(() => {
            if (isAutoSave) {
              service.setState((s) => {
                return { ...s, Education: s.Education.map(elm => elm.id === data.id ? { ...elm, ...data } : elm) };
              });
            }
            return 'Education';
          })
          .catch(error => {
            const { message, stack, code } = error;
            console.error(message, stack, code);
          });
      }
      return errors.length === 0;
    };

    item.delete = (e) => {
      if (e && e.stopPropagation) e.stopPropagation();
      Modal.confirm({
        className: 'delete-item-modal',
        title: <h1 className="Delete-work-experience">Delete education?</h1>,
        content: 'This action cannot be undone.',
        okText: 'Yes, delete',
        okType: 'danger',
        cancelText: 'Cancel',
        iconType: null,
        width: 400,
        onOk: () => {
          if (!item.id) {
            if (props.cancelAddingNewItem) props.cancelAddingNewItem();
            return;
          }
          return service.deleteEducation(item)
            .then(result => {
              loadData(['Education']);
            })
            .catch(error => {
              const { message, stack, code } = error;
              console.error(message, stack, code);
            });
        }
      });
    };
  }, [item]);

  let title = (
    <div className="EducationItem-title" onClick={() => setOpen(!open)}>
      <span className="EducationItem-degree">{item.degree}</span>
      {(open && Array.isArray(Education) && Education.length > 1) ? <span onClick={item.delete} style={{ color: '#F86567' }} className="material-icons" aria-hidden="true">highlight_off</span> : null}
      <span className="material-icons" aria-hidden="true">{open ? 'expand_less' : 'expand_more'}</span>
    </div>
  );

  if (!item.id) {
    title = (
      <div className="EducationItem-title">
        Add education
        <span style={{ flex: 1 }} />
        <span onClick={item.delete} style={{ color: '#F86567' }} className="material-icons" aria-hidden="true">highlight_off</span>
      </div>
    );
  }

  const listDegree = [
    'Bachelors (or equivalent)',
    'Doctorate (or equivalent)',
    'MBA (or equivalent)',
    'Masters (or equivalent)',
    'Secondary/High School (or equivalent)',
    'Other degree'
  ];

  let form = null;
  if (open) {
    form = (
      <div className="EducationItem-form">
        <div className="a"><Input.Select data-cy="finalize-resume-education-degree-select" options={listDegree.map(value => ({ value, label: value }))} label="Level of education" {...bind('level')} /></div>
        <div className="b"><Input data-cy="finalize-resume-education-degree" label="Degree" {...bind('degree')} />{data.degree_error ? <label className='profile-error'>{data.degree_error}</label> : null}</div>
        <div className="c"><Input data-cy="finalize-resume-education-college" label="College" {...bind('college')} />{data.college_error ? <label className='profile-error'>{data.college_error}</label> : null}</div>
        <div className="d EducationItem-date"><Input.Select data-cy="finalize-resume-start-month" options={months} label="Starting date" {...bind('start_month')} /></div>
        <div className="e EducationItem-date"><Input.Select options={years} data-cy="finalize-resume-start-year" label="" {...bind('start_year')} /></div>
        <div className="f EducationItem-date">{data.end_year !== 0 ? <Input.Select data-cy="finalize-resume-end-month" options={months} label="Ending date" {...bind('end_month')} /> : null}</div>
        <div className="g EducationItem-date">{data.end_year !== 0 ? <Input.Select data-cy="finalize-resume-end-year" options={years} label="" {...bind('end_year')} /> : null}</div>
        <div className="h">
          <Checkbox
            data-cy="finalize-resume-still-enrolled"
            checked={data.end_year === 0}
            onChange={(e) => setValue('end_year', e.target.checked ? 0 : new Date().getFullYear())}
          >
            Still enrolled here
          </Checkbox>
        </div>
      </div>
    );
  }

  return (
    <Step title={title} description={form} {...props} />
  );
};

EducationItem.propTypes = {
  item: PropTypes.any,
  cancelAddingNewItem: PropTypes.any,
  service: PropTypes.any
};

export default EducationForm;
